import { Routes } from '@angular/router';
import { NgxPermissionsGuard } from 'ngx-permissions';

import { SearchByIdComponent } from './components/search-by-id/search-by-id.component';
import { AuthGuard } from './guards/auth.guard';

export const routes: Routes = [
  {
    path: 'search-by-id/:id',
    component: SearchByIdComponent,
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'users',
  },
  {
    path: '',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'commission-issuers',
        loadChildren: () =>
          import('./pages/commission-issuers/commission-issuers.module').then(
            (m) => m.CommissionIssuersModule,
          ),
      },
      {
        path: 'cda',
        loadChildren: () =>
          import('./pages/cda/cda.module').then((m) => m.CdaModule),
      },
      {
        path: 'integrations',
        loadChildren: () =>
          import('./pages/integrations/integrations.module').then(
            (m) => m.IntegrationsModule,
          ),
      },
      {
        path: 'logger',
        loadComponent: () =>
          import('./pages/logger-general/logger-general.component').then(
            (m) => m.LoggerGeneralComponent,
          ),
      },
      {
        path: 'promised-payments',
        loadChildren: () =>
          import('./pages/promised-payments/promised-payments.module').then(
            (m) => m.PromisedPaymentsModule,
          ),
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: ['CanViewPayments', 'SuperAdmin'],
          },
        },
      },
      {
        path: 'pricing-policies',
        loadChildren: () =>
          import('./pages/pricing-policies/pricing-policies.module').then(
            (m) => m.PricingPoliciesModule,
          ),
      },
      {
        path: 'marketing-campaigns',
        loadChildren: () =>
          import('./pages/marketing-campaigns/marketing-campaigns.module').then(
            (m) => m.MarketingCampaignsModule,
          ),
      },
      {
        path: 'pricing-policy-rules',
        loadChildren: () =>
          import(
            './pages/pricing-policy-rules/pricing-policy-rules.module'
          ).then((m) => m.PricingPolicyRulesModule),
      },
      {
        path: 'report-users',
        loadChildren: () =>
          import('./pages/report-users/report-users.module').then(
            (m) => m.ReportUsersModule,
          ),
      },
      {
        path: 'broker-users',
        loadChildren: () =>
          import('./pages/broker-users/broker-users.module').then(
            (m) => m.BrokerUsersModule,
          ),
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./pages/users/users.module').then((m) => m.UsersModule),
      },
      {
        path: 'late-payments',
        loadChildren: () =>
          import('./pages/late-payments/late-payments.module').then(
            (m) => m.LatePaymentsModule,
          ),
      },
      {
        path: 'stats',
        loadChildren: () =>
          import('./pages/stats/stats.module').then((m) => m.StatsModule),
      },
      {
        path: 'ach-sftp',
        loadChildren: () =>
          import('./pages/ach-sftp/ach-sftp.module').then(
            (m) => m.AchSftpModule,
          ),
      },
      {
        path: 'svb-ach',
        loadChildren: () =>
          import('./pages/svb-ach/svb-ach.module').then((m) => m.SvbAchModule),
      },
      {
        path: 'svb-ach-batch',
        loadComponent: () =>
          import(
            './pages/svb-ach/components/svb-batch-ach/svb-batch-ach.component'
          ).then((m) => m.SvbBatchAchComponent),
      },
      {
        path: 'svb-v2-ach',
        loadChildren: () =>
          import('./pages/svb-v2-ach/svb-v2-ach.module').then(
            (m) => m.SvbV2AchModule,
          ),
      },
      {
        path: 'deals',
        loadChildren: () =>
          import('./pages/deals/deals.module').then((m) => m.DealsModule),
      },
      {
        path: 'external-deal-commissions',
        loadChildren: () =>
          import(
            './pages/external-deal-commissions/external-deal-commissions.module'
          ).then((m) => m.ExternalDealCommissionsModule),
      },
      {
        path: 'commission-batch-pulls',
        loadChildren: () =>
          import(
            './pages/commission-batch-pulls/commission-batch-pulls.module'
          ).then((m) => m.CommissionBatchPullsModule),
      },
      {
        path: 'facilities',
        loadChildren: () =>
          import('./pages/facilities/facilities.module').then(
            (m) => m.FacilitiesModule,
          ),
      },
      {
        path: 'manage-facilities',
        loadChildren: () =>
          import('./pages/manage-facilities/manage-facilities.module').then(
            (m) => m.ManageFacilitiesModule,
          ),
      },
      {
        path: 'transactions',
        loadChildren: () =>
          import('./pages/transactions/transactions.module').then(
            (m) => m.TransactionsModule,
          ),
      },
      {
        path: 'external-deal',
        loadChildren: () =>
          import(
            './pages/external-deal-details/external-deal-details.module'
          ).then((m) => m.ExternalDealDetailsModule),
      },
      {
        path: 'external-users',
        loadChildren: () =>
          import('./pages/external-user/external-users.module').then(
            (m) => m.ExternalUsersModule,
          ),
      },
      {
        path: 'default-candidates',
        loadChildren: () =>
          import(
            './pages/transactions-default-candidates/transactions-default-candidates.module'
          ).then((m) => m.TransactionsDefaultCandidatesModule),
      },
      {
        path: 'credit-cards',
        loadChildren: () =>
          import('./pages/credit-cards/credit-cards.module').then(
            (m) => m.CreditCardsModule,
          ),
      },
      {
        path: 'accounting',
        loadChildren: () =>
          import('./pages/accounting/accounting.module').then(
            (m) => m.AccountingModule,
          ),
      },
      {
        path: 'brokers',
        loadChildren: () =>
          import('./pages/brokers/brokers.module').then((m) => m.BrokersModule),
      },
      {
        path: 'discounts',
        loadChildren: () =>
          import('./pages/discounts/discounts.module').then(
            (m) => m.DiscountsModule,
          ),
      },
      {
        path: 'deal-promo',
        loadChildren: () =>
          import('./pages/deal-discounts/deal-discounts.module').then(
            (m) => m.DealDiscountsModule,
          ),
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./pages/dashboard/dashboard.module').then(
            (m) => m.DashboardModule,
          ),
      },
      {
        path: 'feature-flags',
        loadChildren: () =>
          import('./pages/feature-flags/feature-flags.module').then(
            (m) => m.FeatureFlagsModule,
          ),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./pages/profile/profile.module').then((m) => m.ProfileModule),
      },
      {
        path: 'tabapay-pull',
        loadChildren: () =>
          import('./pages/tabapay-pull/tabapay-pull.module').then(
            (m) => m.TabapayPullModule,
          ),
      },
      {
        path: 'report-deals-90-days-eligible',
        loadChildren: () =>
          import('./pages/deals/deals-90-days-eligible-list.module').then(
            (m) => m.Deals90DaysEligibleListModule,
          ),
      },
      {
        path: 'payments-overview',
        loadChildren: () =>
          import('./pages/payments-overview/payments-overview.module').then(
            (m) => m.PaymentsOverviewModule,
          ),
      },
      {
        path: 'agreements',
        loadChildren: () =>
          import('./pages/agreements/agreements.module').then(
            (m) => m.AgreementsModule,
          ),
      },
      {
        path: 'paper-check-requests',
        loadChildren: () =>
          import(
            './pages/paper-check-requests/paper-check-requests.module'
          ).then((m) => m.PaperCheckRequestsModule),
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: ['HasWriteAccess', 'SuperAdmin'],
          },
        },
      },
      {
        path: 'reset-sms-validation-cache',
        loadChildren: () =>
          import(
            './pages/reset-sms-validation-cache/reset-sms-validation-cache.module'
          ).then((m) => m.ResetSmsValidationCacheModule),
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: ['CanResetSmsValidationCache', 'SuperAdmin'],
          },
        },
      },
      {
        path: 'astra-fbo-to-tongo-svb-transfers',
        loadChildren: () =>
          import(
            './pages/astra-fbo-to-tongo-svb-transfers/astra-fbo-to-tongo-svb-transfers.module'
          ).then((m) => m.AstraFBOToTongoSVBTransfersModule),
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: ['AllowTransfersFBOToTongoSVB'],
          },
        },
      },
      {
        path: 'agent-payments',
        loadChildren: () =>
          import('./pages/agent-payments/agent-payments.module').then(
            (m) => m.AgentPaymentsModule,
          ),
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            // Strictly CanSendAgentPayments — SuperAdmin does NOT bypass this screen.
            only: ['CanSendAgentPayments'],
          },
        },
      },
      {
        path: 'config',
        loadChildren: () =>
          import('./pages/config/config.module').then((m) => m.ConfigModule),
      },
      {
        path: 'astra',
        loadChildren: () =>
          import('./pages/astra/astra.module').then((m) => m.AstraModule),
      },
    ],
  },
  {
    path: 'login',
    loadChildren: () =>
      import('./pages/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: 'mfa',
    loadComponent: () =>
      import('./pages/mfa/mfa.component').then((m) => m.MfaComponent),
  },
];
